import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import InventoryIcon from '@mui/icons-material/Inventory';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import SecurityIcon from '@mui/icons-material/Security';
import SupportIcon from '@mui/icons-material/Support';
import VerifiedIcon from '@mui/icons-material/Verified';
import logo from '../assets/logo.png';

const features = [
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: '#234734' }} />,
    title: 'Speed up service',
    description: 'Process orders faster with our intuitive interface and quick-access menu.',
  },
  {
    icon: <InventoryIcon sx={{ fontSize: 40, color: '#234734' }} />,
    title: 'Simplify operations',
    description: 'Manage inventory, staff, and orders all in one place.',
  },
  {
    icon: <TableRestaurantIcon sx={{ fontSize: 40, color: '#234734' }} />,
    title: 'Boost profits',
    description: 'Make data-driven decisions with real-time analytics and insights.',
  },
];

const trustMarkers = [
  {
    icon: <SecurityIcon sx={{ fontSize: 30, color: '#F9B233' }} />,
    title: 'Secure & Reliable',
    description: 'Bank-grade security for your data',
  },
  {
    icon: <SupportIcon sx={{ fontSize: 30, color: '#F9B233' }} />,
    title: '24/7 Support',
    description: 'Always here to help you',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 30, color: '#F9B233' }} />,
    title: 'Compliance Ready',
    description: 'Meets industry standards',
  },
];

function Home() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'linear-gradient(90deg, #fff8ec 0%, #F9B233 100%)',
          color: '#234734',
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  color: '#234734',
                  textShadow: '0 2px 8px rgba(249,178,51,0.08)',
                }}
              >
                The Smart POS for Smart Restaurants
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Streamline your restaurant operations with our all-in-one POS solution
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  sx={{
                    bgcolor: '#F9B233',
                    color: '#234734',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(249,178,51,0.10)',
                    '&:hover': { bgcolor: '#ffe1a6', color: '#234734' },
                  }}
                  size="large"
                >
                  Book a Demo
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  sx={{
                    borderColor: '#F9B233',
                    color: '#234734',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#F9B233', color: '#234734', borderColor: '#F9B233' },
                  }}
                  size="large"
                >
                  Start Free Trial
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 180, md: 320 },
                }}
              >
                <img
                  src={logo}
                  alt="Restro360 Logo"
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    borderRadius: '50%',
                    boxShadow: '0 4px 24px rgba(35,71,52,0.10)',
                    background: '#fff8ec',
                    padding: 16,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h2"
          align="center"
          sx={{ mb: 6, color: '#234734', fontWeight: 700 }}
        >
          Why Choose Restro360?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                  bgcolor: '#fff8ec',
                  border: '1.5px solid #F9B233',
                  boxShadow: '0 2px 12px rgba(249,178,51,0.06)',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {feature.icon}
                </Box>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: '#234734', opacity: 0.8 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Trust Markers Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {trustMarkers.map((marker, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    bgcolor: '#fff',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(35,71,52,0.06)',
                    p: 3,
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {marker.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {marker.title}
                  </Typography>
                  <Typography sx={{ color: '#234734', opacity: 0.8 }}>
                    {marker.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: '#234734',
              color: '#F9B233',
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(35,71,52,0.10)',
            }}
          >
            <Typography variant="h3" gutterBottom>
              Ready to Transform Your Restaurant?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of restaurants already using Restro360
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              sx={{
                bgcolor: '#F9B233',
                color: '#234734',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(249,178,51,0.10)',
                '&:hover': { bgcolor: '#ffe1a6', color: '#234734' },
              }}
              size="large"
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home; 